import * as React from 'react';

import { Text } from '@/hen-ui/atomics';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

import {
  linearGaugeIndicatorVariants,
  linearGaugeSegmentVariants,
  linearGaugeValueMarkerCaretVariants,
  linearGaugeValueMarkerVariants,
  linearGaugeVariants,
} from './linear-gauge.variants';

/** Category role for a rendered segment (left→right → category 1, 2, 3). */
type SegmentCategory = 1 | 2 | 3;

/** Default grid weights for a side (prefix/suffix) vs the middle segment. */
const SIDE_SEGMENT_WEIGHT = 7;
const MIDDLE_SEGMENT_WEIGHT = 18;

/**
 * Half of the `spacing/xs` segment gap. Segments render gapless so the `fr`
 * color boundaries coincide with the indicator's percent position; the visible
 * gap is this inset on each bar's inner edges, not a track-consuming grid gap.
 */
const HALF_SEGMENT_GAP = 'calc(var(--space-xs, 12px) / 2)';

/** Treat blank or whitespace-only strings as absent so fallbacks still apply. */
const blankToUndefined = (text: string | undefined): string | undefined => {
  const trimmed = text?.trim();
  return trimmed ? trimmed : undefined;
};

/** Coerce to a finite number, or fall back, so the meter never announces NaN. */
const toFiniteOr = (value: number, fallback: number): number =>
  Number.isFinite(value) ? value : fallback;

export interface LinearGaugeSegment {
  /** Label shown under the segment's left edge when start labels are on. */
  startLabel?: string;
  /** Label shown under the segment's right edge when end labels are on. */
  endLabel?: string;
  /** Show the start label. Default true. */
  showStartLabel?: boolean;
  /** Show the end label. Default true. */
  showEndLabel?: boolean;
  /** Relative grid weight (`fr`). Defaults to a 7:18:7 side:middle:side ratio. */
  weight?: number;
}

export interface LinearGaugeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Value positioned by the indicator along the track. */
  value: number;
  /** Lower bound of the track. Default 0. */
  min?: number;
  /** Upper bound of the track. Default 100. */
  max?: number;
  /** Text shown inside the ValueMarker pill above the dot. */
  markerText?: string;
  /** Give the ValueMarker pill a border + drop shadow. Off by default. */
  markerBordered?: boolean;
  /** Render a leading segment before the middle. Default false. */
  showPrefixSegment?: boolean;
  /** Render a trailing segment after the middle. Default false. */
  showSuffixSegment?: boolean;
  /** Per-segment configuration (1–3). The middle segment always renders. */
  segments?: LinearGaugeSegment[];
  className?: string;
  'aria-label'?: string;
}

interface ResolvedSegment {
  weight: number;
  category: SegmentCategory;
  startLabel?: string;
  endLabel?: string;
  showStartLabel: boolean;
  showEndLabel: boolean;
}

/** Map a rendered segment index to its category role, capped at 3. */
const toCategory = (index: number): SegmentCategory =>
  (Math.min(index, 2) + 1) as SegmentCategory;

/** Position the indicator as a percent of the resolved min/max range. */
const resolveIndicatorPercent = (
  value: number,
  min: number,
  max: number,
): number => {
  const range = max - min;
  if (range <= 0) {
    return 0;
  }
  const clamped = Math.min(Math.max(value, min), max);
  return ((clamped - min) / range) * 100;
};

/** Find which rendered segment the indicator percent lands on. */
const resolveOccupiedIndex = (
  percent: number,
  segments: ResolvedSegment[],
): number => {
  const totalWeight = segments.reduce(
    (sum, segment) => sum + segment.weight,
    0,
  );
  if (totalWeight <= 0) {
    return 0;
  }
  let cumulative = 0;
  for (let index = 0; index < segments.length; index += 1) {
    cumulative += segments[index].weight;
    const boundary = (cumulative / totalWeight) * 100;
    if (percent <= boundary) {
      return index;
    }
  }
  return segments.length - 1;
};

/** Resolve the rendered segments from the flags and the segments config. */
const resolveSegments = (
  showPrefixSegment: boolean,
  showSuffixSegment: boolean,
  segments: LinearGaugeSegment[] | undefined,
): ResolvedSegment[] => {
  const renderedCount =
    1 + (showPrefixSegment ? 1 : 0) + (showSuffixSegment ? 1 : 0);
  const middleIndex = showPrefixSegment ? 1 : 0;

  return Array.from({ length: renderedCount }, (_, index) => {
    const config = segments?.[index] ?? {};
    const isMiddle = index === middleIndex;
    const defaultWeight = isMiddle
      ? MIDDLE_SEGMENT_WEIGHT
      : SIDE_SEGMENT_WEIGHT;
    return {
      weight:
        Number.isFinite(config.weight) && (config.weight as number) > 0
          ? (config.weight as number)
          : defaultWeight,
      category: toCategory(index),
      startLabel: config.startLabel,
      endLabel: config.endLabel,
      showStartLabel: config.showStartLabel ?? true,
      showEndLabel: config.showEndLabel ?? true,
    };
  });
};

/** Internal ValueMarker pill with a downward caret. */
const ValueMarker: React.FC<{
  text: string;
  bordered: boolean;
  percent: number;
}> = ({ text, bordered, percent }) => {
  const caretSide = percent > 50 ? 'end' : 'start';
  return (
    <div
      className={linearGaugeValueMarkerVariants({ bordered, caretSide })}
      style={{ marginLeft: `${percent}%` }}
      data-linear-gauge-marker
      data-caret-side={caretSide}
    >
      <Text tag='span' variant='b2' weight='bold' color='default'>
        {text}
      </Text>
      <svg
        className={linearGaugeValueMarkerCaretVariants({ bordered, caretSide })}
        viewBox='0 0 12 8'
        preserveAspectRatio='none'
        aria-hidden='true'
        focusable='false'
      >
        <polygon points='0,0 6,8 12,0' className='stroke-none' />
        {bordered ? (
          <polyline
            points='0,0 6,8 12,0'
            className='fill-none stroke-1'
            vectorEffect='non-scaling-stroke'
          />
        ) : null}
      </svg>
    </div>
  );
};

/**
 * LinearGauge — domain-neutral single-value gauge over up to three segments.
 * Segment colors read the `data-category` tokens (brand-scale fallback); exposes a `meter` role.
 */
export const LinearGauge = React.forwardRef<HTMLDivElement, LinearGaugeProps>(
  (
    {
      value,
      min = 0,
      max = 100,
      markerText,
      markerBordered = false,
      showPrefixSegment = false,
      showSuffixSegment = false,
      segments,
      className,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const resolved = resolveSegments(
      showPrefixSegment,
      showSuffixSegment,
      segments,
    );
    const safeMin = toFiniteOr(min, 0);
    const safeMax = toFiniteOr(max, 100);
    const safeValue = toFiniteOr(value, safeMin);
    const resolvedMarkerText = blankToUndefined(markerText);
    const resolvedAriaLabel =
      blankToUndefined(ariaLabel) ?? resolvedMarkerText ?? 'Gauge value';
    const percent = resolveIndicatorPercent(safeValue, safeMin, safeMax);
    const clampedValue = Math.min(Math.max(safeValue, safeMin), safeMax);
    const occupiedIndex = resolveOccupiedIndex(percent, resolved);
    const occupiedCategory = resolved[occupiedIndex]?.category ?? 1;
    const gridTemplateColumns = resolved
      .map((segment) => `${segment.weight}fr`)
      .join(' ');
    const hasAnyLabel = resolved.some(
      (segment) =>
        (segment.showStartLabel && Boolean(segment.startLabel)) ||
        (segment.showEndLabel && Boolean(segment.endLabel)),
    );

    return (
      <div
        ref={ref}
        {...rest}
        className={cn(linearGaugeVariants(), className)}
        role='meter'
        aria-valuenow={clampedValue}
        aria-valuemin={safeMin}
        aria-valuemax={safeMax}
        aria-label={resolvedAriaLabel}
        data-hen-id={COMPONENT_IDS.LinearGauge}
      >
        {resolvedMarkerText ? (
          <ValueMarker
            text={resolvedMarkerText}
            bordered={markerBordered}
            percent={percent}
          />
        ) : null}

        <div className='relative w-full'>
          <div className='grid' style={{ gridTemplateColumns }}>
            {resolved.map((segment, index) => (
              <div
                key={index}
                style={{
                  paddingLeft: index === 0 ? undefined : HALF_SEGMENT_GAP,
                  paddingRight:
                    index === resolved.length - 1
                      ? undefined
                      : HALF_SEGMENT_GAP,
                }}
              >
                <div
                  className={linearGaugeSegmentVariants({
                    category: segment.category,
                  })}
                  data-linear-gauge-segment
                />
              </div>
            ))}
          </div>

          <div
            className='absolute top-0 h-1'
            style={{ left: `${percent}%` }}
            data-linear-gauge-indicator-layer
          >
            <span
              className={linearGaugeIndicatorVariants({
                category: occupiedCategory,
              })}
              data-linear-gauge-indicator
            />
          </div>
        </div>

        {hasAnyLabel ? (
          <div
            className='mt-xs grid'
            style={{ gridTemplateColumns }}
            data-linear-gauge-labels
          >
            {resolved.map((segment, index) => (
              <div
                key={index}
                className='flex justify-between gap-xs'
                style={{
                  paddingLeft: index === 0 ? undefined : HALF_SEGMENT_GAP,
                  paddingRight:
                    index === resolved.length - 1
                      ? undefined
                      : HALF_SEGMENT_GAP,
                }}
              >
                <span>
                  {segment.showStartLabel && segment.startLabel ? (
                    <Text tag='span' variant='l1' color='subtext'>
                      {segment.startLabel}
                    </Text>
                  ) : null}
                </span>
                <span>
                  {segment.showEndLabel && segment.endLabel ? (
                    <Text tag='span' variant='l1' color='subtext'>
                      {segment.endLabel}
                    </Text>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

LinearGauge.displayName = 'LinearGauge';
